import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Rocket, ChevronUp, ChevronDown, Pencil, RotateCcw, Loader2, PlayCircle, CheckCircle2, SkipForward,
} from "lucide-react";
import { API } from "../../App";
import { toast } from "sonner";

const authCfg = () => {
  const token = localStorage.getItem("token");
  return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
};

const CATEGORY_CLS = {
  required: "bg-red-500/10 text-red-400 border-red-500/20",
  recommended: "bg-blue-500/10 text-blue-400 border-blue-500/20",
  optional: "bg-muted text-muted-foreground border-theme",
};

const AdminOnboarding = () => {
  const [data, setData] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [editStep, setEditStep] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);

  const fetchSteps = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/onboarding/admin/steps`, authCfg());
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load onboarding steps");
    }
  }, []);

  useEffect(() => { fetchSteps(); }, [fetchSteps]);

  const putStep = async (id, patch) => {
    setBusyId(id);
    try {
      await axios.put(`${API}/onboarding/admin/steps/${id}`, patch, authCfg());
      await fetchSteps();
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
      return false;
    } finally { setBusyId(null); }
  };

  const toggle = async (step, enabled) => {
    const ok = await putStep(step.id, { enabled });
    if (ok) toast.success(`"${step.title}" ${enabled ? "enabled" : "disabled"}`);
  };

  const move = async (idx, dir) => {
    const steps = data.steps;
    const target = idx + dir;
    if (target < 0 || target >= steps.length) return;
    const a = steps[idx], b = steps[target];
    setBusyId(a.id);
    try {
      await Promise.all([
        axios.put(`${API}/onboarding/admin/steps/${a.id}`, { order: b.order }, authCfg()),
        axios.put(`${API}/onboarding/admin/steps/${b.id}`, { order: a.order }, authCfg()),
      ]);
      await fetchSteps();
    } catch (e) {
      toast.error("Reorder failed");
    } finally { setBusyId(null); }
  };

  const reset = async (step) => {
    setBusyId(step.id);
    try {
      await axios.post(`${API}/onboarding/admin/steps/${step.id}/reset`, {}, authCfg());
      toast.success(`"${step.default_title}" reset to defaults`);
      await fetchSteps();
    } catch (e) {
      toast.error("Reset failed");
    } finally { setBusyId(null); }
  };

  const openEdit = (step) => {
    setEditStep(step);
    setEditForm({ title: step.title, description: step.description, category: step.category });
  };

  const saveEdit = async () => {
    setSavingEdit(true);
    const ok = await putStep(editStep.id, editForm);
    setSavingEdit(false);
    if (ok) { toast.success("Step updated"); setEditStep(null); }
  };

  if (!data) {
    return <div className="flex items-center justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  }

  const { steps, stats } = data;
  const enabledCount = steps.filter((s) => s.enabled !== false).length;

  return (
    <div className="space-y-5" data-testid="admin-onboarding-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Rocket className="w-6 h-6 text-[#FFCC00]" /> Onboarding Control
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Manage the guided setup steps new users see. Disabled steps are hidden from every account's wizard and Setup Guide.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "Steps Enabled", value: `${enabledCount} / ${steps.length}`, icon: Rocket, testid: "onb-admin-stat-enabled" },
          { label: "Wizard Starts", value: stats.wizard_starts, icon: PlayCircle, testid: "onb-admin-stat-starts" },
          { label: "Wizard Completes", value: stats.wizard_completes, icon: CheckCircle2, testid: "onb-admin-stat-completes" },
          { label: "Total Step Skips", value: Object.values(stats.step_skips || {}).reduce((a, b) => a + b, 0), icon: SkipForward, testid: "onb-admin-stat-skips" },
        ].map((c) => (
          <Card key={c.label} className="glass-card border-theme">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1"><c.icon className="w-3.5 h-3.5" /> {c.label}</div>
              <p className="text-xl font-bold text-foreground tabular-nums" data-testid={c.testid}>{c.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="glass-card border-theme">
        <CardContent className="p-0">
          <div className="divide-y divide-border">
            {steps.map((s, i) => (
              <div key={s.id} className={`flex items-center gap-3 px-4 py-3 flex-wrap ${s.enabled === false ? "opacity-50" : ""}`} data-testid={`onb-admin-row-${s.id}`}>
                <div className="flex flex-col">
                  <button onClick={() => move(i, -1)} disabled={i === 0 || busyId} className="text-muted-foreground hover:text-foreground disabled:opacity-30" data-testid={`onb-admin-up-${s.id}`}>
                    <ChevronUp className="w-4 h-4" />
                  </button>
                  <button onClick={() => move(i, 1)} disabled={i === steps.length - 1 || busyId} className="text-muted-foreground hover:text-foreground disabled:opacity-30" data-testid={`onb-admin-down-${s.id}`}>
                    <ChevronDown className="w-4 h-4" />
                  </button>
                </div>
                <span className="text-xs text-muted-foreground w-6 tabular-nums">{i + 1}</span>
                <div className="flex-1 min-w-[200px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-foreground">{s.title}</span>
                    <Badge variant="outline" className={`text-[10px] ${CATEGORY_CLS[s.category] || CATEGORY_CLS.optional}`}>{s.category}</Badge>
                    {s.applies !== "both" && (
                      <Badge variant="outline" className="text-[10px] bg-purple-500/10 text-purple-400 border-purple-500/20">
                        {s.applies === "multi" ? "Multi-location only" : "Single-location only"}
                      </Badge>
                    )}
                    {s.is_overridden && (
                      <Badge variant="outline" className="text-[10px] bg-amber-500/10 text-amber-400 border-amber-500/20" data-testid={`onb-admin-custom-${s.id}`}>Customized</Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{s.description}</p>
                </div>
                <span className="text-[11px] text-muted-foreground tabular-nums w-16 text-right" title="Times skipped by users">
                  {(stats.step_skips || {})[s.id] || 0} skips
                </span>
                <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-foreground" onClick={() => openEdit(s)} data-testid={`onb-admin-edit-${s.id}`}>
                  <Pencil className="w-3.5 h-3.5" />
                </Button>
                {s.is_overridden && (
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-foreground" onClick={() => reset(s)} title="Reset to defaults" data-testid={`onb-admin-reset-${s.id}`}>
                    <RotateCcw className="w-3.5 h-3.5" />
                  </Button>
                )}
                <Switch
                  checked={s.enabled !== false}
                  disabled={busyId === s.id}
                  onCheckedChange={(v) => toggle(s, v)}
                  data-testid={`onb-admin-toggle-${s.id}`}
                />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!editStep} onOpenChange={(o) => !o && setEditStep(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Edit Step — {editStep?.default_title}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Title</Label>
              <Input value={editForm.title || ""} onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))} data-testid="onb-admin-edit-title" />
            </div>
            <div>
              <Label className="text-xs">Description</Label>
              <Textarea rows={3} value={editForm.description || ""} onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))} data-testid="onb-admin-edit-description" />
            </div>
            <div>
              <Label className="text-xs">Category</Label>
              <Select value={editForm.category} onValueChange={(v) => setEditForm((f) => ({ ...f, category: v }))}>
                <SelectTrigger data-testid="onb-admin-edit-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="required">Required</SelectItem>
                  <SelectItem value="recommended">Recommended</SelectItem>
                  <SelectItem value="optional">Optional</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditStep(null)}>Cancel</Button>
            <Button onClick={saveEdit} disabled={savingEdit} className="bg-[#FFCC00] hover:bg-[#e6b800] text-black font-semibold" data-testid="onb-admin-save-edit">
              {savingEdit && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminOnboarding;
